import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import { Minus, Plus, ArrowLeft, ShoppingBag, Check, Loader2 } from 'lucide-react';
import { API } from '@/config';

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [displayImage, setDisplayImage] = useState('');
  const { addToCart, loading: cartLoading } = useCart();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${id}`);
        setProduct(response.data);
        setDisplayImage(response.data.image_url);
        // Select first variant by default if available
        if (response.data.fabric_variants && response.data.fabric_variants.length > 0) {
          setSelectedVariant(response.data.fabric_variants[0]);
          setDisplayImage(response.data.fabric_variants[0].image_url);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  const handleVariantSelect = (variant) => {
    setSelectedVariant(variant);
    setDisplayImage(variant.image_url);
  };

  const handleAddToCart = async () => {
    const success = await addToCart(
      product.id, 
      selectedVariant?.id || null, 
      quantity
    );
    if (success) {
      toast.success(`${product.name} added to cart!`);
    } else {
      toast.error('Failed to add item to cart');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-pockett-purple" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="text-muted-foreground text-lg mb-4">Product not found</p>
        <Button asChild variant="outline" className="rounded-full">
          <Link to="/shop">Back to Shop</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 md:py-12 bg-pattern">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Back Button */}
        <Link 
          to="/shop"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-pockett-purple transition-colors mb-8"
          data-testid="back-to-shop"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Shop
        </Link>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Image Section */}
          <div className="space-y-4 animate-fade-in">
            {/* Main Image */}
            <div className="aspect-square rounded-2xl overflow-hidden bg-muted">
              <img 
                src={displayImage} 
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-main-image"
              />
            </div>
            
            {/* Thumbnail Gallery */}
            {product.fabric_variants && product.fabric_variants.length > 0 && (
              <div className="flex gap-3 overflow-x-auto pb-2">
                <button
                  onClick={() => {
                    setSelectedVariant(null);
                    setDisplayImage(product.image_url);
                  }}
                  className={`w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all ${
                    !selectedVariant ? 'border-pockett-purple' : 'border-transparent hover:border-border'
                  }`}
                  data-testid="thumbnail-default"
                >
                  <img 
                    src={product.image_url} 
                    alt="Default"
                    className="w-full h-full object-cover"
                  />
                </button>
                {product.fabric_variants.map((variant) => (
                  <button
                    key={variant.id}
                    onClick={() => handleVariantSelect(variant)}
                    className={`w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all ${
                      selectedVariant?.id === variant.id 
                        ? 'border-pockett-purple' 
                        : 'border-transparent hover:border-border'
                    }`}
                    data-testid={`thumbnail-${variant.id}`}
                  >
                    <img 
                      src={variant.image_url} 
                      alt={variant.name}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6 animate-fade-in animate-delay-100">
            <div>
              <p className="text-sm text-muted-foreground uppercase tracking-wide mb-2">
                {product.category}
              </p>
              <h1 
                className="font-heading font-bold text-3xl md:text-4xl text-pockett-gray mb-4"
                data-testid="product-name"
              >
                {product.name}
              </h1>
              <p 
                className="font-heading font-bold text-2xl text-pockett-purple"
                data-testid="product-price"
              >
                ${product.price.toFixed(2)} AUD
              </p>
            </div>

            <div className="text-muted-foreground leading-relaxed whitespace-pre-line" data-testid="product-description">
              {product.description}
            </div>

            {/* Fabric Variants */}
            {product.fabric_variants && product.fabric_variants.length > 0 && (
              <div>
                <h3 className="font-medium text-pockett-gray mb-3">
                  Select Fabric Print
                  {selectedVariant && (
                    <span className="text-muted-foreground font-normal ml-2">
                      — {selectedVariant.name}
                    </span>
                  )}
                </h3>
                <div className="flex flex-wrap gap-3">
                  {product.fabric_variants.map((variant) => (
                    <button
                      key={variant.id}
                      onClick={() => handleVariantSelect(variant)}
                      className={`variant-swatch relative w-12 h-12 rounded-full overflow-hidden border-2 ${
                        selectedVariant?.id === variant.id 
                          ? 'selected border-pockett-purple' 
                          : 'border-border hover:border-pockett-purple/50'
                      }`}
                      title={variant.name}
                      data-testid={`variant-${variant.id}`}
                    >
                      <img 
                        src={variant.image_url} 
                        alt={variant.name}
                        className="w-full h-full object-cover"
                      />
                      {selectedVariant?.id === variant.id && (
                        <div className="absolute inset-0 bg-pockett-purple/20 flex items-center justify-center">
                          <Check className="h-5 w-5 text-white drop-shadow-md" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div>
              <h3 className="font-medium text-pockett-gray mb-3">Quantity</h3>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-full h-10 w-10"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                  data-testid="decrease-quantity"
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span 
                  className="w-12 text-center font-medium text-lg"
                  data-testid="quantity-display"
                >
                  {quantity}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-full h-10 w-10"
                  onClick={() => setQuantity(quantity + 1)}
                  data-testid="increase-quantity"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Add to Cart */}
            <div className="pt-4 space-y-3">
              <Button
                className="w-full bg-pockett-purple hover:bg-pockett-purple/90 rounded-full h-14 font-semibold text-lg"
                onClick={handleAddToCart}
                disabled={cartLoading}
                data-testid="add-to-cart-btn"
              >
                {cartLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <ShoppingBag className="h-5 w-5 mr-2" />
                    Add to Cart — ${(product.price * quantity).toFixed(2)}
                  </>
                )}
              </Button>
            </div>

            {/* Additional Info */}
            <div className="pt-6 border-t border-border space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-pockett-purple/10 flex items-center justify-center flex-shrink-0">
                  <Check className="h-4 w-4 text-pockett-purple" />
                </div>
                <div>
                  <p className="font-medium text-pockett-gray">Handmade with Love</p>
                  <p className="text-sm text-muted-foreground">Every piece is crafted by hand</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-pockett-pink/10 flex items-center justify-center flex-shrink-0">
                  <Check className="h-4 w-4 text-pockett-pink" />
                </div>
                <div>
                  <p className="font-medium text-pockett-gray">Quality Materials</p>
                  <p className="text-sm text-muted-foreground">Made with premium fabrics</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
